import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '$lib/components/ui/Input';
import { useHotkey } from '$lib/hooks/useHotkey';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSwitcher } from './LanguageSwitcher';

// Top bar — brand left, address search center, status + theme/language toggles right.
// Address search is the M3-scope command-palette substitute (decision 2) and
// the M4 entry point for valuation (address-search-as-target, DEV-108).
//
// Behaviour:
//   - Press "/" anywhere to focus the input.
//   - Press Enter to navigate to /search?q=… — the search route does the
//     resolve + provisional-fallback handshake against the address API.

export function TopBar() {
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const { t } = useTranslation();

  // Restore the query string from the URL so the input matches the search
  // route the user landed on (helps when sharing a link).
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.pathname === '/search') {
      setValue(url.searchParams.get('q') ?? '');
    }
  }, []);

  useHotkey({ key: '/' }, (e) => {
    // Don't steal "/" when the user is already typing somewhere else.
    if (document.activeElement === inputRef.current) return;
    e.preventDefault();
    inputRef.current?.focus();
    inputRef.current?.select();
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const q = value.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }

  return (
    <header
      data-testid="topbar"
      className="flex h-12 shrink-0 items-center gap-3 border-b border-rule bg-surface px-3"
    >
      <div className="flex items-center gap-2">
        <span aria-hidden className="grid h-6 w-6 place-items-center rounded-sm bg-accent text-accent-fg text-xs font-semibold">
          R
        </span>
        <span className="text-sm font-semibold tracking-tight text-fg">RentalLens</span>
        <span className="text-2xs text-fg-subtle">{t('topbar.tag')}</span>
      </div>

      <form className="ml-6 flex max-w-xl flex-1 items-center" role="search" onSubmit={onSubmit}>
        <label htmlFor="address-search" className="sr-only">
          {t('topbar.searchAriaLabel')}
        </label>
        <div className="relative w-full">
          <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <Input
            id="address-search"
            data-testid="address-search"
            ref={inputRef}
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('topbar.searchPlaceholder')}
            className="pl-7"
          />
        </div>
      </form>

      <div className="ml-auto flex items-center gap-2">
        <span className="text-2xs text-fg-subtle">{t('topbar.version')}</span>
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
    </header>
  );
}
